/**
 * Fantasy Console 0 - Shared Tool Bar Component
 *
 * Generic tool selection bar with icons and keyboard shortcuts.
 * Used by both sprite and map editors.
 */

import { useCallback } from 'react'
import styles from './ToolBar.module.css'
import editorStyles from '../../styles/editor.module.css'

export interface ToolDef<T extends string> {
  id: T
  label: string
  shortcut: string
  icon: React.ReactNode
}

interface ToolBarProps<T extends string> {
  tools: ToolDef<T>[]
  activeTool: T
  onSelectTool: (tool: T) => void
  label?: string
}

export function ToolBar<T extends string>({
  tools,
  activeTool,
  onSelectTool,
  label = 'Tools',
}: ToolBarProps<T>) {
  const handleToolClick = useCallback(
    (tool: T) => {
      onSelectTool(tool)
    },
    [onSelectTool],
  )

  return (
    <div className={styles.container}>
      {label ?? <span className={styles.label}>{label}</span>}
      <div className={editorStyles.toolGroup}>
        {tools.map((tool) => (
          <button
            key={tool.id}
            className={`${editorStyles.toolButton} ${activeTool === tool.id ? editorStyles.toolButtonActive : ''}`}
            onClick={() => handleToolClick(tool.id)}
            title={`${tool.label} (${tool.shortcut})`}
            aria-label={tool.label}
            aria-pressed={activeTool === tool.id}
          >
            {tool.icon}
            <span className={editorStyles.toolShortcut}>{tool.shortcut}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
